import React, { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileUploader from '../components/FileUploader';
import JDPreview from '../components/JDPreview';
import SampleShowcase from '../components/SampleShowcase';
import {
  uploadFiles,
  createCheckout,
  PRICE_PER_RESUME_CENTS,
  UploadResult,
} from '../api/screening.api';
import { stashCheckout } from '../api/checkoutStash';

type AppState = 'upload' | 'ready' | 'redirecting';

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function ScreeningPage() {
  const [state, setState] = useState<AppState>('upload');
  const [uploadData, setUploadData] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  const handleFilesSelected = useCallback(async (files: File[]) => {
    setError(null);
    setState('upload');
    setStatusMsg('Uploading and parsing files...');
    setProgress(30);

    try {
      const data = await uploadFiles(files);
      setUploadData(data);

      if (!data.jd) {
        setError(
          'No job description detected. Include a file with "job", "JD", "description", or "requirements" in the filename.',
        );
        setState('upload');
      } else if (data.resumes.length === 0) {
        setError('No parseable resumes found in the folder.');
        setState('upload');
      } else {
        setState('ready');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
      setState('upload');
    }
    setProgress(0);
    setStatusMsg('');
  }, []);

  const handleCheckout = useCallback(async () => {
    if (!uploadData?.jdText || !uploadData.resumesFull) return;

    setState('redirecting');
    setError(null);
    setStatusMsg('Creating secure checkout…');

    try {
      const count = uploadData.resumesFull.length;
      const checkout = await createCheckout(count);

      // Stash the upload state so /process can run screening after Stripe
      // redirects back. sessionStorage survives the round trip; React state
      // doesn't.
      stashCheckout({
        sessionId: checkout.sessionId,
        uploadData,
        customInstructions,
        allEvaluations: [],
        count: checkout.count,
        amountCents: checkout.amountCents,
      });

      // Off to Stripe (or, in dev mode, straight to /process).
      window.location.href = checkout.checkoutUrl;
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'Checkout failed',
      );
      setState('ready');
      setStatusMsg('');
    }
  }, [uploadData, customInstructions]);

  const handleReset = useCallback(() => {
    setState('upload');
    setUploadData(null);
    setError(null);
    setCustomInstructions('');
  }, []);

  const activeStep =
    state === 'upload' ? 0 : state === 'ready' ? 1 : state === 'redirecting' ? 1 : 2;

  const resumeCount = uploadData?.resumes.length || 0;
  const totalCents = resumeCount * PRICE_PER_RESUME_CENTS;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          StrixScreener
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
          AI Resume Screening
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Select Folder</StepLabel>
        </Step>
        <Step>
          <StepLabel>Pay & Screen</StepLabel>
        </Step>
        <Step>
          <StepLabel>Results</StepLabel>
        </Step>
      </Stepper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Progress */}
      {(state === 'redirecting' || progress > 0) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant={state === 'redirecting' ? 'indeterminate' : 'determinate'}
            value={progress}
          />
          {statusMsg && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {statusMsg}
            </Typography>
          )}
        </Box>
      )}

      {/* Step 1: Upload */}
      {(state === 'upload' || state === 'ready') && (
        <FileUploader
          onFilesSelected={handleFilesSelected}
          disabled={false}
        />
      )}

      {/* Landing showcase: only on the initial upload state, before any
          files have been selected. Hidden once the user has an upload
          in flight / ready / redirecting, to keep the tool flow clean. */}
      {state === 'upload' && !uploadData && <SampleShowcase />}

      {/* Step 2: Ready to checkout */}
      {state === 'ready' && uploadData && (
        <Box sx={{ mt: 3 }}>
          <JDPreview
            jdName={uploadData.jd?.name || 'Unknown'}
            jdPreview={uploadData.jd?.preview || ''}
            resumeCount={resumeCount}
            warnings={uploadData.warnings}
            skippedJds={uploadData.skippedJds}
          />

          <TextField
            label="Custom Instructions (optional)"
            placeholder="e.g., Prioritize hands-on ClearPass experience. Weight multi-vendor exposure highly."
            multiline
            rows={3}
            fullWidth
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            sx={{ mt: 2 }}
            size="small"
          />

          <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
            <Typography variant="body2" color="text.secondary">
              {resumeCount} resume{resumeCount !== 1 ? 's' : ''} × {formatDollars(PRICE_PER_RESUME_CENTS)} each
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {formatDollars(totalCents)} total
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Secure checkout via Stripe. Card-only. No account needed.
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<LockIcon />}
              onClick={handleCheckout}
              sx={{ px: 4 }}
            >
              Pay {formatDollars(totalCents)} & Screen {resumeCount} Resume
              {resumeCount !== 1 ? 's' : ''}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {/* Redirecting to Stripe */}
      {state === 'redirecting' && (
        <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Redirecting to secure checkout…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stripe will charge you {formatDollars(totalCents)} for {resumeCount} resume
            {resumeCount !== 1 ? 's' : ''}. After payment, screening starts automatically.
          </Typography>
        </Paper>
      )}
    </Container>
  );
}
